import React, { useCallback, useMemo } from "react";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { BannerType } from "lib/types/checkout";
import { hexToRgba } from "lib/utils/helpers";
import { isLightColor, shadeColor } from "@shopflo/ui";
import { useMerchantUiConfig } from "lib/contexts/MerchantUIConfigProvider";
import { AlertTriangle } from "react-feather";
import { useJsonPermission } from "lib/hooks/useJsonPermission";

interface CartBannerBottomProps {
  bannerPosition?: BannerType;
}

const CartBannerStrip: React.FC<CartBannerBottomProps> = ({ bannerPosition = BannerType.HEADER }) => {
  const {
    state: { cartMetadata },
  } = useMerchantUiConfig();

  const { cartPermissions } = useJsonPermission();

  const bannerConfig = useMemo(() => {
    const cartAttributes = cartMetadata?.ui_attributes;
    const bannersConfig =
      bannerPosition === BannerType.HEADER
        ? cartAttributes?.banner_config
        : cartAttributes?.bottom_banner_config;
    if (!bannersConfig) return null;

    let currentBanner = bannersConfig.find((banner) => banner.type === "CART");

    if (!currentBanner || !currentBanner.enabled) return null;

    return {
      htmlString: currentBanner.html_text_string ?? "",
      backgroundColor: currentBanner.background_color ?? "black",
      textColor: currentBanner.text_color ?? "white",
    };
  }, [bannerPosition, cartMetadata]);

  const getBorderColor = useCallback((backgroundColor: string) => {
    return hexToRgba(backgroundColor.substring(0, 7) ?? "#000000", 0.5);
  }, []);

  return (
    <div className="sticky top-0 z-20">
      {bannerConfig && (
        <div
          className={`flex min-h-[33px] w-full items-center justify-center px-3 py-2 text-center text-sm ${
            bannerPosition === BannerType.HEADER ? "rounded-b-xl" : "rounded-t-xl"
          }`}
          style={{
            backgroundColor: bannerConfig.backgroundColor,
            color: bannerConfig.textColor,
            boxShadow: `0px ${
              bannerPosition === BannerType.HEADER ? "1" : "-1"
            }px 5px 0px rgba(0, 0, 0, 0.2)`,
            borderBottom:
              bannerPosition === BannerType.HEADER
                ? `1px solid ${getBorderColor(bannerConfig.backgroundColor)}`
                : "",
            borderTop:
              bannerPosition === BannerType.FOOTER
                ? `1px solid ${getBorderColor(bannerConfig.backgroundColor)}`
                : "",
          }}
          dangerouslySetInnerHTML={{ __html: bannerConfig.htmlString }}
        />
      )}
      {bannerPosition === BannerType.FOOTER && cartPermissions?.checkoutBtnStatus && (
        <div className="flex items-start gap-2 min-h-[33px] w-full justify-center px-3 py-2 text-center text-sm bg-coal-dark">
          <div>
            <AlertTriangle className="h-4 w-4 text-yellow-light" />
          </div>
          <span className="text-white">{cartPermissions?.checkoutBtnStatus}</span>
        </div>
      )}
    </div>
  );
};

export default CartBannerStrip;
